import { useReducer, useState, useMemo, useContext, useRef, useEffect, memo } from 'react';
import type { ComponentType, Dispatch, Context, MutableRefObject, SetStateAction } from 'react';
import { createPortal } from 'react-dom';

import { createUID, windowIsAvailable } from '../../utils';
import '../../init';
import { useConfiguration } from '../../hooks';
import type { DeferInfer } from '../../types';

import PreviewPanel from './PreviewPanel';
import type {
  PreviewOptions,
  PreviewManagerContextValue,
  PreviewMethods,
  PreviewObject,
  PreviewManagerProps
} from './Preview.types';
import { PreviewContext, PreviewManagerContext } from './Contexts';

declare module '../../init' {
  export interface CosmosGlobals {
    previewManagerContext?: Context<PreviewManagerContextValue>;
  }
}

type PreviewReducerPayload<P extends object> =
  | { type: 'create'; preview: PreviewObject<P> }
  | { type: 'update'; id: string; props: Partial<P>; options?: Partial<PreviewOptions> }
  | { type: 'unmount'; id: string };

const reducer = <P extends object>(
  previews: PreviewObject[] | [],
  payload: PreviewReducerPayload<P>
): PreviewObject<object>[] => {
  switch (payload.type) {
    case 'create':
      // Stub for multiple preview support
      // return [...previews, payload.preview as unknown as PreviewObject<object>];
      return [payload.preview as unknown as PreviewObject<object>];

    case 'update':
      return previews.map(preview =>
        preview.id === payload.id
          ? {
              ...preview,
              props: { ...preview.props, ...payload.props },
              options: {
                ...preview.options,
                ...payload.options,
                previewProps: {
                  ...preview.options?.previewProps,
                  ...payload.options?.previewProps
                }
              }
            }
          : preview
      );

    case 'unmount':
      return previews.filter(preview => preview.id !== payload.id);

    default:
      return previews;
  }
};

const WrappedPreview = memo(
  ({
    Component,
    props,
    options,
    previewStateSetter,
    previewTriggerRef,
    methods
  }: PreviewObject & {
    previewStateSetter: Dispatch<SetStateAction<boolean>>;
    previewTriggerRef: MutableRefObject<HTMLElement | null>;
  }) => {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    // This state change is required to transition the case preview in.
    const [open, setOpen] = useState(false);
    const openRef = useRef(false);

    const cleanupPreview = () => {
      setOpen(false);
      openRef.current = false;
      previewStateSetter(false);
      optionsRef.current?.previewProps.onAfterClose?.();
      methods.unmountPreview();
    };

    useEffect(() => {
      setOpen(true);
      openRef.current = true;
      previewStateSetter(true);
      return () => {
        // Guard with openRef so we don't double-fire if already closed via the close button.
        if (openRef.current) {
          optionsRef.current?.previewProps.onBeforeClose?.();
          cleanupPreview();
        }
      };
    }, []);

    return (
      <PreviewContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          initialized: true,
          previewTriggerRef,
          ...methods
        }}
      >
        <PreviewPanel
          open={open}
          {...options?.previewProps}
          onAfterClose={() => {
            cleanupPreview();
          }}
        >
          <Component {...(props as Record<string, unknown>)} />
        </PreviewPanel>
      </PreviewContext.Provider>
    );
  }
);

const PreviewManager = ({ children }: PreviewManagerProps) => {
  const [previews, dispatch] = useReducer(reducer, []);
  const [previewActive, setPreviewActive] = useState(false);
  const previewTriggerRef = useRef<HTMLElement | null>(null);
  const { portalTarget } = useConfiguration();

  const providerValue = useMemo(
    () => ({
      previewActive,
      setPreviewActive,
      createPreview: <P extends object>(
        Component: ComponentType<P>,
        props?: DeferInfer<P>,
        options?: PreviewOptions
      ): PreviewMethods<P> => {
        const id = createUID();

        const methods = {
          updatePreview: (updatedProps: Partial<P>, updatedOptions?: Partial<PreviewOptions>) => {
            (dispatch as Dispatch<PreviewReducerPayload<P>>)({
              type: 'update',
              id,
              props: updatedProps,
              options: updatedOptions
            });
          },
          unmountPreview: () => {
            dispatch({ type: 'unmount', id });
          }
        };

        (dispatch as Dispatch<PreviewReducerPayload<P>>)({
          type: 'create',
          preview: {
            id,
            Component,
            methods,
            props: (props ?? {}) as P,
            options
          }
        });

        return methods;
      },
      PreviewContext,
      initialized: true
    }),
    [previewActive]
  );

  const context = windowIsAvailable
    ? window.cosmos.previewManagerContext ?? PreviewManagerContext
    : PreviewManagerContext;
  const priorCtx = useContext(context);

  // Don't create additional Preview managers.
  if (priorCtx.initialized) return <context.Provider value={priorCtx}>{children}</context.Provider>;

  const previewPortal =
    portalTarget && previews.length > 0
      ? createPortal(
          previews.map(preview => (
            <WrappedPreview
              {...preview}
              key={preview.id}
              previewStateSetter={setPreviewActive}
              previewTriggerRef={previewTriggerRef}
            />
          )),
          portalTarget
        )
      : null;

  return (
    <context.Provider value={providerValue}>
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
      <PreviewContext.Provider value={{ initialized: false, previewTriggerRef }}>
        {children}
        {previewPortal}
      </PreviewContext.Provider>
    </context.Provider>
  );
};

export default PreviewManager;
